import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SequenceStep, SequenceStepType } from "@sales-ops-amplify/shared";
import { cn } from "@/cn";
import {
  enrollInSequence,
  getSequence,
  patchSequenceEnrollment,
  updateSequence,
} from "@/api/sequences";
import { listLeads } from "@/api/leads";

type Tab = "overview" | "steps" | "leads" | "settings";

const STEP_TYPES: SequenceStepType[] = [
  "auto_email",
  "manual_email",
  "call",
  "action_item",
  "linkedin_step_connect",
  "linkedin_step_message",
];

function stepLabel(t: SequenceStepType): string {
  return t.replace(/_/g, " ");
}

export function SequencesDetailPage() {
  const { sequenceId = "" } = useParams<{ sequenceId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("overview");
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(
    new Set(),
  );
  const [message, setMessage] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["sequences", sequenceId],
    queryFn: () => getSequence(sequenceId),
    enabled: Boolean(sequenceId),
  });

  const contactsQuery = useQuery({
    queryKey: ["leads", "all"],
    queryFn: () => listLeads(),
    enabled: enrollOpen,
  });

  const sequence = data?.sequence;
  const enrollments = data?.enrollments ?? [];

  const saveMutation = useMutation({
    mutationFn: (fields: Parameters<typeof updateSequence>[1]) =>
      updateSequence(sequenceId, fields),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sequences"] });
      setMessage("Saved.");
    },
  });

  const enrollMutation = useMutation({
    mutationFn: () =>
      enrollInSequence(sequenceId, {
        leadIds: [...selectedContacts],
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["sequences", sequenceId] });
      setEnrollOpen(false);
      setSelectedContacts(new Set());
      setMessage(
        `Enrolled ${res.enrollments.length}; skipped ${res.skipped.length}.`,
      );
    },
    onError: (err: unknown) => {
      setMessage(err instanceof Error ? err.message : "Enroll failed");
    },
  });

  const sortedSteps = useMemo(
    () =>
      [...(sequence?.steps ?? [])].sort((a, b) => a.position - b.position),
    [sequence?.steps],
  );

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading sequence…</p>;
  }
  if (error || !sequence) {
    return (
      <p className="text-sm text-red-600">
        {error instanceof Error ? error.message : "Sequence not found"}
      </p>
    );
  }

  const updateStep = (index: number, patch: Partial<SequenceStep>) => {
    const steps = sortedSteps.map((s, i) =>
      i === index ? { ...s, ...patch } : s,
    );
    saveMutation.mutate({ steps });
  };

  const addStep = () => {
    const steps = [
      ...sortedSteps,
      {
        id: crypto.randomUUID(),
        position: sortedSteps.length,
        type: "action_item" as const,
        waitTime: 1,
        waitMode: "day" as const,
        enabled: true,
        note: "New step",
        touches: [],
      },
    ];
    saveMutation.mutate({ steps });
  };

  return (
    <div className="mx-auto max-w-5xl">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to="/sequences"
            className="text-xs text-muted-foreground hover:text-brand"
          >
            ← Sequences
          </Link>
          <h1 className="mt-1 text-2xl font-bold text-foreground">
            {sequence.name}
          </h1>
          <p className="text-sm text-muted-foreground">
            {sequence.active ? "Active" : "Inactive"} ·{" "}
            {sequence.stats.numSteps} steps ·{" "}
            {sequence.stats.activeEnrollments} active contacts
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() =>
              saveMutation.mutate({ active: !sequence.active })
            }
            className={cn(
              "rounded-md px-3 py-1.5 text-sm font-medium",
              sequence.active
                ? "border border-border"
                : "bg-brand text-brand-foreground",
            )}
          >
            {sequence.active ? "Deactivate" : "Activate"}
          </button>
          <button
            type="button"
            onClick={() => setEnrollOpen(true)}
            className="rounded-md border border-border px-3 py-1.5 text-sm"
          >
            Add contacts
          </button>
        </div>
      </header>

      {message ? (
        <p className="mb-4 text-sm text-muted-foreground">{message}</p>
      ) : null}

      <div className="mb-4 flex flex-wrap gap-1 border-b border-border">
        {(
          [
            ["overview", "Overview"],
            ["steps", "Steps"],
            ["leads", "Leads"],
            ["settings", "Settings"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={cn(
              "border-b-2 px-3 py-2 text-sm font-medium",
              tab === id
                ? "border-brand text-foreground"
                : "border-transparent text-muted-foreground",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "overview" ? (
        <section className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {(
            [
              ["Active", sequence.stats.activeEnrollments],
              ["Finished", sequence.stats.finishedEnrollments],
              ["Scheduled", sequence.stats.uniqueScheduled],
              ["Delivered", sequence.stats.uniqueDelivered],
              ["Replied", sequence.stats.uniqueReplied],
              ["Bounced", sequence.stats.uniqueBounced],
            ] as const
          ).map(([label, value]) => (
            <div
              key={label}
              className="rounded-lg border border-border bg-card p-3"
            >
              <p className="text-xs uppercase text-muted-foreground">{label}</p>
              <p className="mt-1 text-xl font-semibold tabular-nums">{value}</p>
            </div>
          ))}
        </section>
      ) : null}

      {tab === "steps" ? (
        <section className="space-y-3">
          {sortedSteps.map((step, index) => (
            <div
              key={step.id}
              className="rounded-lg border border-border bg-card p-4"
            >
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-xs font-medium text-muted-foreground">
                  Step {index + 1}
                </span>
                <select
                  className="rounded-md border border-border bg-background px-2 py-1 text-sm"
                  value={step.type}
                  onChange={(e) =>
                    updateStep(index, {
                      type: e.target.value as SequenceStepType,
                    })
                  }
                >
                  {STEP_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {stepLabel(t)}
                    </option>
                  ))}
                </select>
                <label className="flex items-center gap-1 text-xs text-muted-foreground">
                  Wait
                  <input
                    type="number"
                    min={0}
                    className="w-16 rounded border border-border bg-background px-1 py-0.5"
                    value={step.waitTime}
                    onChange={(e) =>
                      updateStep(index, {
                        waitTime: Number(e.target.value) || 0,
                      })
                    }
                  />
                  <select
                    className="rounded border border-border bg-background px-1 py-0.5"
                    value={step.waitMode}
                    onChange={(e) =>
                      updateStep(index, {
                        waitMode: e.target.value as SequenceStep["waitMode"],
                      })
                    }
                  >
                    <option value="day">days</option>
                    <option value="hour">hours</option>
                    <option value="minute">minutes</option>
                  </select>
                </label>
                <label className="flex items-center gap-1 text-xs">
                  <input
                    type="checkbox"
                    checked={step.enabled}
                    onChange={(e) =>
                      updateStep(index, { enabled: e.target.checked })
                    }
                  />
                  Enabled
                </label>
              </div>
              {(step.type === "auto_email" || step.type === "manual_email") &&
              step.touches[0] ? (
                <div className="mt-3 space-y-2">
                  <input
                    className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                    value={step.touches[0].subject ?? ""}
                    placeholder="Subject"
                    onChange={(e) => {
                      const touches = [...step.touches];
                      touches[0] = { ...touches[0], subject: e.target.value };
                      updateStep(index, { touches });
                    }}
                  />
                  <textarea
                    className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                    rows={3}
                    value={step.touches[0].bodyHtml ?? ""}
                    placeholder="Body HTML"
                    onChange={(e) => {
                      const touches = [...step.touches];
                      touches[0] = { ...touches[0], bodyHtml: e.target.value };
                      updateStep(index, { touches });
                    }}
                  />
                </div>
              ) : (
                <textarea
                  className="mt-3 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                  rows={2}
                  value={step.note ?? ""}
                  placeholder="Notes / script"
                  onChange={(e) => updateStep(index, { note: e.target.value })}
                />
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={addStep}
            className="rounded-md border border-dashed border-border px-3 py-2 text-sm text-muted-foreground hover:bg-muted/20"
          >
            + Add step
          </button>
        </section>
      ) : null}

      {tab === "leads" ? (
        <section>
          <div className="mb-3 flex justify-end">
            <button
              type="button"
              onClick={() => setEnrollOpen(true)}
              className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground"
            >
              Add contacts
            </button>
          </div>
          <div className="min-w-0 overflow-x-auto overscroll-x-contain rounded-lg border border-border [-webkit-overflow-scrolling:touch]">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border bg-muted/30 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Contact</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Step</th>
                  <th className="px-3 py-2">Next due</th>
                  <th className="px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {enrollments.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-3 py-8 text-center text-muted-foreground"
                    >
                      No enrollments yet.
                    </td>
                  </tr>
                ) : (
                  enrollments.map((e) => (
                    <tr key={e.enrollmentId} className="border-b border-border/70">
                      <td className="px-3 py-2">
                        <Link
                          to={`/user/leads?q=${encodeURIComponent(e.leadName ?? "")}`}
                          className="hover:text-brand"
                        >
                          {e.leadName ?? e.leadId}
                        </Link>
                        {e.accountId ? (
                          <p className="text-xs text-muted-foreground">
                            {e.accountId}
                          </p>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 capitalize">{e.status}</td>
                      <td className="px-3 py-2 tabular-nums">
                        {e.currentStepPosition + 1}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {e.nextDueAt
                          ? new Date(e.nextDueAt).toLocaleString()
                          : "—"}
                      </td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          className="text-xs text-brand hover:underline"
                          onClick={() =>
                            patchSequenceEnrollment(
                              sequenceId,
                              e.leadId,
                              {
                                status:
                                  e.status === "paused" ? "active" : "paused",
                              },
                            ).then(() =>
                              qc.invalidateQueries({
                                queryKey: ["sequences", sequenceId],
                              }),
                            )
                          }
                        >
                          {e.status === "paused" ? "Resume" : "Pause"}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {tab === "settings" ? (
        <section className="max-w-lg space-y-4 rounded-lg border border-border bg-card p-4">
          <label className="block text-sm">
            Name
            <input
              className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5"
              defaultValue={sequence.name}
              onBlur={(e) => {
                if (e.target.value.trim() !== sequence.name) {
                  saveMutation.mutate({ name: e.target.value.trim() });
                }
              }}
            />
          </label>
          <fieldset className="space-y-2 text-sm">
            <legend className="font-medium">Rules</legend>
            {(
              [
                ["finishOnReply", "Finish if reply"],
                ["pauseOnOoo", "Pause if OOO"],
                ["finishOnMeetingBooked", "Finish if meeting booked"],
              ] as const
            ).map(([key, label]) => (
              <label key={key} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={sequence.rules[key]}
                  onChange={(e) =>
                    saveMutation.mutate({
                      rules: { ...sequence.rules, [key]: e.target.checked },
                    })
                  }
                />
                {label}
              </label>
            ))}
          </fieldset>
          <label className="block text-sm">
            Timezone
            <input
              className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5"
              defaultValue={sequence.schedule.timezone}
              onBlur={(e) =>
                saveMutation.mutate({
                  schedule: {
                    ...sequence.schedule,
                    timezone: e.target.value.trim() || "UTC",
                  },
                })
              }
            />
          </label>
          <button
            type="button"
            className="text-sm text-red-600 hover:underline"
            onClick={() => {
              if (!confirm("Archive this sequence?")) return;
              saveMutation.mutate({ archived: true, active: false });
              navigate("/sequences");
            }}
          >
            Archive sequence
          </button>
        </section>
      ) : null}

      {enrollOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[80vh] w-full max-w-lg overflow-auto rounded-lg border border-border bg-card p-5 shadow-lg">
            <h2 className="text-lg font-semibold">Add contacts</h2>
            {!sequence.active ? (
              <p className="mt-2 text-sm text-amber-600">
                Activate the sequence before enrolling contacts.
              </p>
            ) : null}
            <ul className="mt-3 max-h-80 space-y-1 overflow-auto">
              {(contactsQuery.data?.people ?? []).map((c) => (
                <li key={c.leadId}>
                  <label className="flex items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted/30">
                    <input
                      type="checkbox"
                      checked={selectedContacts.has(c.leadId)}
                      onChange={() => {
                        setSelectedContacts((prev) => {
                          const next = new Set(prev);
                          if (next.has(c.leadId)) next.delete(c.leadId);
                          else next.add(c.leadId);
                          return next;
                        });
                      }}
                    />
                    <span>
                      {c.name}
                      <span className="text-muted-foreground">
                        {" "}
                        · {c.accountId}
                      </span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEnrollOpen(false)}
                className="rounded-md border border-border px-3 py-1.5 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={
                  !sequence.active ||
                  selectedContacts.size === 0 ||
                  enrollMutation.isPending
                }
                onClick={() => enrollMutation.mutate()}
                className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground disabled:opacity-50"
              >
                Enroll {selectedContacts.size || ""}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
